import ButtonWithIcon from '@/components/common/ButtonWithIcon';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import {
    ATM,
    FileDownload03,
    Mail02,
    Trash01,
} from '@/components/common/icons';
import { InfoButtonWithModal } from '@/components/common/InfoModal';
import Notice from '@/components/common/Notice';
import PlentyFullScreenSpinner from '@/components/common/PlentyFullScreenSpinner';
import { useToasts } from '@/components/common/Toasts';
import {
    BigHeader,
    Header,
    Paragraph,
    Subheader,
    Text,
} from '@/components/common/Typography';
import { showConfirmCloseAtomicAccountModal } from '@/components/export/ConfirmCloseAtomicAccountModal';
import GoalLockup from '@/components/goals/GoalLockup';
import SettingsLayout from '@/components/settings/SettingsLayout';
import { logError } from '@/util/logger';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Spacer } from '@chakra-ui/react';
import {
    AnyDepositoryAccount,
    isDepositoryAccount,
    isExternalFinancialEntity,
} from '@common/financialEntities';
import { Goal } from '@common/goals';
import {
    AtomicAccountFieldsFragment,
    AtomicAccountsDocument,
    ExportDataDocument,
    SendPershingCredentialsDocument,
    UpdateUserProfileDocument,
} from '@common/graphql/generated';
import { useFinancialEntitiesContext } from '@common/react/financialEntities';
import { TOASTS } from '@common/toasts';
import { sum } from '@common/util/arrays';
import { toDollars, toDollarsWithCents } from '@common/util/currency';
import pluralize from '@common/util/pluralize';
import { capitalize } from '@common/util/strings';
import { isNullish } from '@common/util/types';

const ExportPage = () => {
    const [exportData, { loading: isSubmitting }] =
        useMutation(ExportDataDocument);
    const [updateUserProfile] = useMutation(UpdateUserProfileDocument);
    const { showSuccessToast, showErrorToast } = useToasts();
    const { fes } = useFinancialEntitiesContext();
    const { data: atomicAccountsData, loading: atomicAccountsLoading } =
        useQuery(AtomicAccountsDocument);

    if (
        !fes.unfilteredData ||
        fes.loading ||
        atomicAccountsLoading ||
        !atomicAccountsData?.atomicAccounts
    ) {
        return <PlentyFullScreenSpinner />;
    }

    const depositoryAccounts = fes.unfilteredData.financialEntities
        .filter((fe) => fe.ownersIncludeMyself && isNullish(fe.deletedAt))
        .filter(isExternalFinancialEntity)
        .filter(isDepositoryAccount);

    const accounts = atomicAccountsData.atomicAccounts;
    const total = sum(accounts, (a) => a.totalValue);
    const jointAccount = accounts.find((a) => a.accountType === 'JOINT');
    const individualAccount = accounts.find(
        (a) => a.accountType === 'INDIVIDUAL'
    );
    const hasOneAccount = jointAccount || individualAccount;
    const hasBothAccounts = jointAccount && individualAccount;
    const accountsLabel = hasBothAccounts
        ? 'Individual & Joint accounts'
        : jointAccount
          ? 'Joint account'
          : 'Individual account';

    return (
        <SettingsLayout>
            <FlexCol gap={8}>
                <BigHeader fontSize="3xl">
                    We&apos;re saying goodbye. Thank you for being a part of
                    Plenty.
                </BigHeader>

                <Paragraph color="gray.600">
                    Plenty is transitioning to Wealthsimple, and we
                    couldn&apos;t have gotten here without you. As we prepare to
                    close, we want to ensure you have everything you need.
                    Please take a moment to download your data
                    {total > 0 ? '  and withdraw your funds ' : ''} to wrap
                    things up smoothly.
                </Paragraph>

                <FlexCol gap={4}>
                    <BigHeader>Important - action needed</BigHeader>
                    <Text>
                        Plenty&apos;s product will be closing, but your data
                        {total > 0 ? ' and money are ' : ' is '}
                        always yours. Please follow these steps to export your
                        data
                        {total > 0 ? ' and withdraw your money.' : '.'}
                    </Text>
                </FlexCol>

                <FlexCol gap={4}>
                    <Header>
                        {hasOneAccount ? '1.' : ''} Export your data
                    </Header>
                    <Text>
                        Download a full record of your balances and transactions
                        before Plenty closes.
                    </Text>
                    <ButtonWithIcon
                        isLoading={isSubmitting}
                        iconLeft={<FileDownload03 />}
                        onClickEvent="Download Data Button Clicked"
                        onClick={async () => {
                            try {
                                const { data } = await exportData({});
                                if (data?.exportData?.csvUrl) {
                                    window.open(data.exportData.csvUrl);
                                    showSuccessToast(
                                        'Data exported successfully'
                                    );
                                    await updateUserProfile({
                                        variables: {
                                            patch: {
                                                hasExportedData: true,
                                            },
                                        },
                                    });
                                }
                            } catch (e) {
                                logError(e);
                                showErrorToast(TOASTS.unexpectedError);
                            }
                        }}
                        w="fit-content"
                    >
                        Download my data
                    </ButtonWithIcon>

                    <Paragraph color="gray.500" fontSize="sm">
                        If no action is taken, you will no longer be able to
                        download your data. You will be emailed access to all
                        relevant tax documents, for those who have invested with
                        Plenty.
                    </Paragraph>
                </FlexCol>

                {hasOneAccount && (
                    <>
                        <FlexCol gap={4}>
                            <Header>
                                2.{' '}
                                {total > 0
                                    ? 'Withdraw your money from your'
                                    : 'Close your'}{' '}
                                {accountsLabel}
                            </Header>
                            <Text>
                                You currently have {toDollarsWithCents(total)}{' '}
                                in your {accountsLabel} with Plenty.{' '}
                                {total > 0
                                    ? 'Please withdraw all funds and close'
                                    : 'Please close'}{' '}
                                your{' '}
                                {pluralize('account', accounts.length, false)}{' '}
                                before Plenty closes.
                            </Text>

                            <FlexRow gap={6}>
                                {jointAccount && (
                                    <CloseAccountBlock
                                        accountType="joint"
                                        account={jointAccount}
                                        goals={fes.unfilteredData.goals}
                                        depositoryAccounts={depositoryAccounts}
                                    />
                                )}

                                {individualAccount && (
                                    <CloseAccountBlock
                                        accountType="individual"
                                        account={individualAccount}
                                        goals={fes.unfilteredData.goals}
                                        depositoryAccounts={depositoryAccounts}
                                    />
                                )}
                            </FlexRow>

                            <Paragraph color="gray.500" fontSize="sm">
                                If no action is taken, Plenty will place
                                liquidating trades and transfer the funds at
                                settlement to your linked bank account.
                            </Paragraph>
                        </FlexCol>

                        <FlexCol gap={4}>
                            <Header>
                                3. Set up a Pershing account to access tax
                                documents
                            </Header>
                            <Paragraph>
                                After Plenty shuts down, you’ll still be able to
                                access your tax documents through our custodian,
                                Pershing.{' '}
                                <Text as="span" fontWeight="semibold">
                                    To ensure document access for future tax
                                    seasons, make sure to set up your Pershing
                                    account before Plenty closes.
                                </Text>{' '}
                                You&apos;ll need to do this for each account you
                                have with Plenty.
                            </Paragraph>

                            <FlexRow gap={6}>
                                {jointAccount && (
                                    <EmailAccountCredentialsBlock
                                        accountType="joint"
                                        account={jointAccount}
                                    />
                                )}

                                {individualAccount && (
                                    <EmailAccountCredentialsBlock
                                        accountType="individual"
                                        account={individualAccount}
                                    />
                                )}
                            </FlexRow>
                        </FlexCol>
                    </>
                )}
            </FlexCol>
        </SettingsLayout>
    );
};

const EmailAccountCredentialsBlock = ({
    accountType,
    account,
}: {
    accountType: 'joint' | 'individual';
    account: AtomicAccountFieldsFragment;
}) => {
    const { showSuccessToast, showErrorToast } = useToasts();
    const [sendPershingCredentials, { loading: isSubmitting }] = useMutation(
        SendPershingCredentialsDocument
    );

    return (
        <Box p={6} borderWidth={1} borderRadius="md" flex={1} maxW={400}>
            <FlexCol gap={4} h="full">
                <Subheader>{capitalize(accountType)} account</Subheader>
                <ButtonWithIcon
                    isLoading={isSubmitting}
                    iconLeft={<Mail02 />}
                    onClickEvent="Setup Pershing Account Button Clicked"
                    onClick={async () => {
                        try {
                            await sendPershingCredentials({
                                variables: {
                                    accountId: account.accountId,
                                },
                            });
                            showSuccessToast(
                                'Credentials sent successfully. Check your email, and be sure to look in your spam folder'
                            );
                        } catch (e) {
                            logError(e);
                            showErrorToast(
                                TOASTS.unexpectedErrorPleaseReachOut
                            );
                        }
                    }}
                    w="fit-content"
                >
                    Email me setup instructions
                </ButtonWithIcon>
            </FlexCol>
        </Box>
    );
};

const CloseAccountBlock = ({
    accountType,
    account,
    goals,
    depositoryAccounts,
}: {
    accountType: 'joint' | 'individual';
    account: AtomicAccountFieldsFragment;
    goals: Goal[];
    depositoryAccounts: AnyDepositoryAccount[];
}) => {
    const filteredGoals = goals.filter((g) => account.goalIds.includes(g.id));

    return (
        <Box p={6} borderWidth={1} borderRadius="md" flex={1} maxW={400}>
            <FlexCol gap={4} h="full">
                <Subheader>{capitalize(accountType)} account</Subheader>
                <FlexCol gap={2}>
                    <Text>
                        <Text as="span" fontWeight="semibold">
                            Total balance:
                        </Text>{' '}
                        {toDollarsWithCents(account.totalValue)}
                    </Text>
                    <FlexRow gap={1} align="center">
                        <Text>
                            <Text as="span" fontWeight="semibold">
                                Est. unrealized gain/loss:
                            </Text>{' '}
                            {toDollars(account.unrealizedPnl, {
                                withCents: true,
                                alwaysSigned: true,
                            })}
                        </Text>
                        <InfoButtonWithModal
                            title="Estimated unrealized gain/loss"
                            buttonProps={{
                                onClickEventParams: {
                                    location: 'Estimated gains/loss',
                                },
                            }}
                        >
                            <Text>
                                This figure represents the difference between
                                the current market value of your investment and
                                its cost basis. It is intended to provide an
                                estimate of potential capital gains or losses if
                                the investment were sold today.
                            </Text>
                            <Text>
                                Unrealized gains are profits that have not yet
                                been realized through a sale and may be subject
                                to capital gains taxes if sold. Unrealized
                                losses represent a decline in value and could
                                offset gains when realized (thereby, reducing
                                taxes). This is an estimate for informational
                                purposes only and should not be considered tax
                                advice. The actual tax consequences depend on
                                your specific situation, holding period, and tax
                                bracket.
                            </Text>
                        </InfoButtonWithModal>
                    </FlexRow>
                    <FlexCol mt={4} gap={2}>
                        {filteredGoals.map((goal) => (
                            <Box
                                key={goal.id}
                                border="1px solid"
                                borderColor="gray.200"
                                rounded="md"
                                p={2}
                            >
                                <GoalLockup goal={goal} />
                            </Box>
                        ))}
                    </FlexCol>
                </FlexCol>
                <Spacer />
                {account.accountStatus === 'APPROVED' ? (
                    <ButtonWithIcon
                        iconLeft={
                            account.totalValue > 0 ? <ATM /> : <Trash01 />
                        }
                        onClickEvent="Confirm Close Atomic Account Button Clicked"
                        onClick={() =>
                            showConfirmCloseAtomicAccountModal({
                                atomicAccountId: account.accountId,
                                selectableDepositoryAccounts:
                                    depositoryAccounts,
                                hasBalance: account.totalValue > 0,
                                accountType,
                            })
                        }
                        w="fit-content"
                    >
                        {account.totalValue > 0
                            ? 'Withdraw all & close'
                            : 'Close'}
                    </ButtonWithIcon>
                ) : account.accountStatus === 'CLOSING' ? (
                    <Notice>
                        Your account closure request is processing. Withdrawals
                        will arrive in multiple transfers over a few business
                        days as we liquidate any investments. Future dividends
                        will also be automatically forwarded to your designated
                        account.
                    </Notice>
                ) : (
                    <Notice>Your account has been successfully closed.</Notice>
                )}
            </FlexCol>
        </Box>
    );
};

export default ExportPage;
